import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircleDollarSign, Sparkles, ShieldCheck, ArrowRight, Eye, Users, Minus, Plus, AlertCircle } from 'lucide-react';
import api, { createStripeCheckoutSession, publishCollabOffer } from '../../../services/api';
import { useAuth } from '../../auth/AuthContext';
import {
  TYPES,
  FOLLOWER_REQUIREMENTS,
  STATIC_EXCHANGE_OPTIONS,
  STATIC_TARGET_OPTIONS,
  DEFAULT_COUNTRIES,
  FORM_STEPS,
  emptyForm,
  parseDateOnly,
  formatDateInputValue,
  normalizeCollabPayload,
  validateCollabStep,
  validateAllCollabSteps,
} from './collabFormShared';
import {
  scrollToAndFocusFirstError,
  normalizeBackendErrors,
  isImageFileValid,
} from '../collabValidationHelper';
import AICampaignAssistant from '../AICampaignAssistant';
import './FirstCollabActivation.scss';

const AI_GUIDE_STORAGE_KEY = 'foth-first-collab-ai-guide-dismissed-v1';

export const FirstCollabActivation = ({ initialMode = null, onExit = null }) => {
  const { t } = useTranslation('collab');
  const { currentUser, subscriptionInfo } = useAuth();
  const aiGuideStorageKey = `${AI_GUIDE_STORAGE_KEY}:${currentUser?.id ?? currentUser?.username ?? 'anonymous'}`;
  const navigate = useNavigate();
  const location = useLocation();

  const maxCreators = Number(
    subscriptionInfo?.has_active_subscription
      ? (subscriptionInfo?.max_creators_per_collab ??
        subscriptionInfo?.active_subscription?.max_creators_per_collab ??
        0)
      : 0
  );

  const searchMode = new URLSearchParams(location.search).get('mode');
  const effectiveMode = initialMode || searchMode;
  const initialCollabType = effectiveMode === 'paid' ? 'paid' : 'gifting';
  const initialScene = effectiveMode === 'paid' ? 'form' : 'overview';

  const [scene, setScene] = useState(initialScene); // 'overview' | 'type_selection' | 'form'
  const [collabType, setCollabType] = useState(initialCollabType); // 'gifting' | 'paid'
  const [currentStep, setCurrentStep] = useState(1); // 1, 2, 3
  const [formData, setFormData] = useState(() => emptyForm(initialCollabType));
  const [errors, setErrors] = useState({});

  // Assets / Previews
  const [selectedFeaturedFile, setSelectedFeaturedFile] = useState(null);
  const [featuredPreviewUrl, setFeaturedPreviewUrl] = useState('');
  const [selectedAdditionalFiles, setSelectedAdditionalFiles] = useState([]);

  // Remote data lists
  const [countries, setCountries] = useState(DEFAULT_COUNTRIES);
  const [exchangeOptions, setExchangeOptions] = useState(STATIC_EXCHANGE_OPTIONS);
  const [targetOptions, setTargetOptions] = useState(STATIC_TARGET_OPTIONS);
  const [plans, setPlans] = useState([]);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  // States
  const [createdOfferId, setCreatedOfferId] = useState(null);
  const [isAIAssistantOpen, setIsAIAssistantOpen] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });
  const [aiGuideDismissed, setAiGuideDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(aiGuideStorageKey) === '1';
    } catch {
      return false;
    }
  });

  const aiButtonRef = useRef(null);
  const fileInputRef = useRef(null);
  const additionalFileInputRef = useRef(null);

  useEffect(() => {
    try {
      setAiGuideDismissed(window.localStorage.getItem(aiGuideStorageKey) === '1');
    } catch {
      setAiGuideDismissed(false);
    }
  }, [aiGuideStorageKey]);

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      if (featuredPreviewUrl && featuredPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(featuredPreviewUrl);
      }
    };
  }, [featuredPreviewUrl]);

  // Load initial reference data (countries, plans, options)
  useEffect(() => {
    let isMounted = true;
    const loadInitialData = async () => {
      try {
        const [countriesRes, exchangeRes, targetRes, plansRes] = await Promise.allSettled([
          api.get('/countries/'),
          api.get('/collabs/exchange-options/'),
          api.get('/collabs/target-options/'),
          api.get('/plans/'),
        ]);

        if (!isMounted) return;

        if (countriesRes.status === 'fulfilled' && Array.isArray(countriesRes.value.data)) {
          setCountries(countriesRes.value.data.map((c) => [String(c.id), c.name]));
        }
        if (exchangeRes.status === 'fulfilled' && Array.isArray(exchangeRes.value.data) && exchangeRes.value.data.length) {
          setExchangeOptions(exchangeRes.value.data);
        }
        if (targetRes.status === 'fulfilled' && Array.isArray(targetRes.value.data) && targetRes.value.data.length) {
          setTargetOptions(targetRes.value.data);
        }
        if (plansRes.status === 'fulfilled') {
          const allPlans = Array.isArray(plansRes.value.data)
            ? plansRes.value.data
            : (plansRes.value.data?.results || []);
          const plansData = allPlans.filter((plan) => !plan?.plan_type || plan.plan_type === 'subscription');
          setPlans(plansData);

          const defaultPlan = plansData.find((p) => (p.is_highlighted || p.is_popular) && p.billing_period === 'monthly')
            || plansData.find((p) => p.billing_period === 'monthly')
            || plansData[0];
          if (defaultPlan) {
            setSelectedPlanId(defaultPlan.id);
          }
        }
      } catch (err) {
        console.error('Error loading reference data:', err);
      }
    };

    loadInitialData();
    return () => { isMounted = false; };
  }, []);

  const showToastMessage = useCallback((message, type = 'success') => {
    setToast({ visible: true, message, type });
    window.clearTimeout(window.__fothToastTimer);
    window.__fothToastTimer = window.setTimeout(() => {
      setToast({ visible: false, message: '', type: 'success' });
    }, 4000);
  }, []);

  // Handlers for Navigation & Scenes
  const handleExit = () => {
    if (onExit) {
      onExit();
    } else {
      navigate('/dashboard');
    }
  };

  const handleStart = () => {
    setScene('type_selection');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectCollabType = (type) => {
    setCollabType(type);
    setFormData((prev) => ({
      ...prev,
      compensation_mode: type === 'paid' ? 'paid' : 'exchange',
      sponsor_this_offer: type === 'paid',
    }));
    setCurrentStep(1);
    setScene('form');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToOverview = () => {
    setScene('overview');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDismissAiGuide = () => {
    setAiGuideDismissed(true);
    try {
      window.localStorage.setItem(aiGuideStorageKey, '1');
    } catch {
      // Ignore
    }
  };

  const handleOpenAI = () => {
    handleDismissAiGuide();
    setIsAIAssistantOpen(true);
  };

  const handleCloseAI = () => {
    setIsAIAssistantOpen(false);
    if (aiButtonRef.current) {
      aiButtonRef.current.focus();
    }
  };

  const handleApplyAIData = (data) => {
    const nextFromDate = parseDateOnly(data.from_date);
    const nextToDate = parseDateOnly(data.to_date);

    setFormData((prev) => ({
      ...prev,
      title: data.title || prev.title,
      value: data.value ?? prev.value,
      quantity: maxCreators > 0 && Number(data.quantity) > maxCreators ? maxCreators : (data.quantity || prev.quantity),
      description: data.description ? data.description : prev.description,
      type: data.type || prev.type,
      followers_requirements: data.followers_requirements || prev.followers_requirements,
      exchange_options: Array.isArray(data.exchange_options) && data.exchange_options.length ? data.exchange_options.map(String) : prev.exchange_options,
      deliverables_mode: data.deliverables_mode || prev.deliverables_mode,
      deliverables_counts: data.deliverables_mode === 'open' ? {} : (data.deliverables_counts && Object.keys(data.deliverables_counts).length ? data.deliverables_counts : prev.deliverables_counts),
      country: data.country || prev.country,
      from_date: nextFromDate ? nextFromDate.toISOString().slice(0, 10) : prev.from_date,
      to_date: nextToDate ? nextToDate.toISOString().slice(0, 10) : prev.to_date,
      add_time_range: data.add_time_range !== undefined ? data.add_time_range : prev.add_time_range,
      target_options: Array.isArray(data.target_options) && data.target_options.length ? data.target_options.map(String) : prev.target_options,
      terms_and_conditions: data.terms_and_conditions || prev.terms_and_conditions,
      sponsor_amount: data.sponsor_amount || prev.sponsor_amount,
    }));
    setErrors({});
    handleCloseAI();
    showToastMessage('FOTH AI aplicó los datos al formulario editable.');
  };

  const handleExample = (type = 'gifting') => {
    const today = new Date();
    const from = new Date(today);
    from.setDate(from.getDate() + 2);
    const to = new Date(from);
    to.setDate(to.getDate() + 14);

    const isPaid = type === 'paid';
    setCollabType(type);
    setFormData({
      ...emptyForm(type),
      title: isPaid ? 'Paid creator launch' : 'Lanzamiento de colección cápsula',
      type: '2',
      value: isPaid ? '100' : '120',
      sponsor_amount: isPaid ? '150.00' : '',
      quantity: isPaid ? 4 : 5,
      country: countries[0] ? countries[0][0] : '11',
      from_date: from.toISOString().slice(0, 10),
      to_date: to.toISOString().slice(0, 10),
      description: isPaid
        ? 'Campaña pagada para presentar la nueva colección a una audiencia de moda y lifestyle.'
        : 'Invitación a probar la colección cápsula y crear contenido auténtico para la marca.',
      followers_requirements: '3',
      deliverables_mode: 'defined',
      exchange_options: ['1', '2'],
      deliverables_counts: { '1': 1, '2': 3 },
      target_options: ['1', '2', '4'],
      terms_and_conditions: 'Publicar durante las fechas de la campaña, mencionar a la marca y entregar el contenido dentro del plazo acordado.',
      sponsor_this_offer: isPaid,
      compensation_mode: isPaid ? 'paid' : 'exchange',
    });
    setCurrentStep(1);
    setScene('form');
    showToastMessage(`Ejemplo de ${isPaid ? 'Paid' : 'Gifting'} Collab cargado.`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Form Field Change Handlers
  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field] || (field === 'from_date' && errors.dates) || (field === 'to_date' && errors.dates)) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        if (field === 'from_date' || field === 'to_date') delete next.dates;
        return next;
      });
    }
  };

  const handleToggleChange = (field, checked) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: checked };
      if (field === 'sponsor_this_offer' && collabType === 'gifting') {
        next.compensation_mode = checked ? 'hybrid' : 'exchange';
      }
      return next;
    });
  };

  const handleDeliverablesModeChange = (mode) => {
    setFormData((prev) => ({
      ...prev,
      deliverables_mode: mode,
      deliverables_counts: mode === 'defined' ? prev.deliverables_counts : {},
    }));
  };

  const handleDeliverableToggle = (id) => {
    setFormData((prev) => {
      const exists = prev.exchange_options.includes(id);
      const nextOptions = exists
        ? prev.exchange_options.filter((item) => item !== id)
        : [...prev.exchange_options, id];
      const nextCounts = { ...prev.deliverables_counts };
      if (exists) {
        delete nextCounts[id];
      } else if (prev.deliverables_mode === 'defined' && nextCounts[id] == null) {
        nextCounts[id] = 1;
      }
      return {
        ...prev,
        exchange_options: nextOptions,
        deliverables_counts: nextCounts,
      };
    });
    if (errors.exchange_options || errors.deliverables_counts) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.exchange_options;
        delete next.deliverables_counts;
        return next;
      });
    }
  };

  const handleDeliverableCountChange = (id, countVal) => {
    const parsed = Math.max(0, Number.parseInt(countVal || '0', 10));
    setFormData((prev) => ({
      ...prev,
      deliverables_counts: {
        ...prev.deliverables_counts,
        [id]: Number.isNaN(parsed) ? 0 : parsed,
      },
    }));
    if (errors.deliverables_counts) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.deliverables_counts;
        return next;
      });
    }
  };

  const handleTargetToggle = (id) => {
    setFormData((prev) => {
      const exists = prev.target_options.includes(id);
      return {
        ...prev,
        target_options: exists
          ? prev.target_options.filter((item) => item !== id)
          : [...prev.target_options, id],
      };
    });
    if (errors.target_options) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.target_options;
        return next;
      });
    }
  };

  // Image Upload Handlers
  const handleFeaturedImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isImageFileValid(file)) {
      setErrors((prev) => ({
        ...prev,
        featured_image: t('create.errors.invalid_image_format', 'Solo se permiten imágenes en formato JPG o PNG.'),
      }));
      e.target.value = '';
      return;
    }

    if (featuredPreviewUrl && featuredPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(featuredPreviewUrl);
    }
    const previewUrl = URL.createObjectURL(file);
    setSelectedFeaturedFile(file);
    setFeaturedPreviewUrl(previewUrl);
    setFormData((prev) => ({ ...prev, featured_image: file }));

    if (errors.featured_image) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.featured_image;
        return next;
      });
    }
    e.target.value = '';
  };

  const handleRemoveFeaturedImage = () => {
    if (featuredPreviewUrl && featuredPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(featuredPreviewUrl);
    }
    setSelectedFeaturedFile(null);
    setFeaturedPreviewUrl('');
    setFormData((prev) => ({ ...prev, featured_image: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAdditionalImagesUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const validFiles = files.filter(isImageFileValid);
    if (validFiles.length !== files.length) {
      showToastMessage(t('create.errors.invalid_image_format', 'Solo se permiten imágenes en formato JPG o PNG.'), 'error');
    }
    if (!validFiles.length) {
      e.target.value = '';
      return;
    }

    const merged = [...selectedAdditionalFiles, ...validFiles].slice(0, 5);
    setSelectedAdditionalFiles(merged);
    setFormData((prev) => ({ ...prev, additional_images: merged }));
    e.target.value = '';
  };

  const handleRemoveAdditionalImage = (index) => {
    const updated = selectedAdditionalFiles.filter((_, i) => i !== index);
    setSelectedAdditionalFiles(updated);
    setFormData((prev) => ({ ...prev, additional_images: updated }));
    if (additionalFileInputRef.current) {
      additionalFileInputRef.current.value = '';
    }
  };

  // Step Validation & Stepper Navigation
  const validateCurrentStepOnly = (stepNum = currentStep) => {
    const result = validateCollabStep({
      step: stepNum,
      formData,
      mode: collabType,
      maxCreators,
      t,
      hasFeaturedPreview: Boolean(featuredPreviewUrl || formData.featured_image),
    });

    if (!result.isValid) {
      setErrors(result.errors);
      scrollToAndFocusFirstError(result.errors, stepNum, 'first-collab');
      return false;
    }
    setErrors({});
    return true;
  };

  const handleNextStep = () => {
    if (!validateCurrentStepOnly(currentStep)) return;

    if (currentStep < 3) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      handleOpenPricingModal();
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      setErrors({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Draft Saving
  const persistDraft = async () => {
    const { payload } = normalizeCollabPayload(formData, collabType);
    const response = createdOfferId
      ? await api.put(`/collabs/collab/${createdOfferId}/`, payload)
      : await api.post('/collabs/collab/', payload);
    const offerId = response.data?.id || createdOfferId;
    if (!offerId) throw new Error('No se pudo identificar el collab guardado.');
    setCreatedOfferId(offerId);
    return offerId;
  };

  const handleSaveDraft = async () => {
    if (isSavingDraft) return;
    setIsSavingDraft(true);
    try {
      await persistDraft();
      showToastMessage('Tu borrador se ha guardado correctamente.');
    } catch (err) {
      const backendErrors = normalizeBackendErrors(err.response?.data);
      if (Object.keys(backendErrors).length) {
        setErrors(backendErrors);
      }
      showToastMessage(err.response?.data?.error || 'Error al guardar el borrador.', 'error');
    } finally {
      setIsSavingDraft(false);
    }
  };

  // Open Pricing Modal / Publish Flow
  const handleOpenPricingModal = async () => {
    if (isSavingDraft) return;
    // Validate entire form before proceeding to publish/plan
    const fullValidation = validateAllCollabSteps({
      formData,
      mode: collabType,
      maxCreators,
      t,
      hasFeaturedPreview: Boolean(featuredPreviewUrl || formData.featured_image),
    });

    if (!fullValidation.isValid) {
      setErrors(fullValidation.errors);
      if (fullValidation.firstInvalidStep && fullValidation.firstInvalidStep !== currentStep) {
        setCurrentStep(fullValidation.firstInvalidStep);
      }
      setTimeout(() => {
        scrollToAndFocusFirstError(fullValidation.errors, fullValidation.firstInvalidStep || currentStep, 'first-collab');
      }, 100);
      showToastMessage('Completa los campos requeridos antes de continuar.', 'error');
      return;
    }

    // Persist the latest fields and files even when an earlier draft exists.
    let offerId;
    setIsSavingDraft(true);
    try {
      offerId = await persistDraft();
    } catch (err) {
      setErrors(normalizeBackendErrors(err.response?.data));
      showToastMessage('Error al guardar el collab antes de continuar.', 'error');
      return;
    } finally {
      setIsSavingDraft(false);
    }

    // Check if user already has an active subscription
    if (subscriptionInfo?.has_active_subscription) {
      if (collabType === 'paid' || formData.sponsor_this_offer) {
        // Paid collabs require creator-funding checkout
        navigate(`/business/collabs/${offerId}/journey`);
        return;
      }
      try {
        await publishCollabOffer(offerId);
        showToastMessage('¡Collab publicado con éxito!');
        navigate(`/business/collabs/${offerId}/journey`);
        return;
      } catch (err) {
        if (err.response?.status === 402) {
          setIsPricingModalOpen(true);
        } else {
          showToastMessage(err.response?.data?.error || 'Error al publicar el collab.', 'error');
        }
      }
      return;
    }

    // Open real plan selection modal
    setIsPricingModalOpen(true);
  };

  const handleClosePricingModal = useCallback(() => {
    if (isProcessingPayment) return;
    setIsPricingModalOpen(false);
  }, [isProcessingPayment]);

  // Handle pricing modal body lock and escape key
  useEffect(() => {
    if (!isPricingModalOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isProcessingPayment) {
        handleClosePricingModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPricingModalOpen, isProcessingPayment, handleClosePricingModal]);

  // Synchronize plan selection when billing period changes
  const handleSelectBillingPeriod = (nextPeriod) => {
    if (nextPeriod === billingPeriod) return;
    const currentPlan = plans.find((p) => p.id === selectedPlanId);

    const matchingPlan = plans.find((p) => {
      if (p.billing_period !== nextPeriod) return false;
      if (currentPlan && p.max_collabs === currentPlan.max_collabs) return true;
      if (currentPlan) {
        const isBoutique = currentPlan.name.toLowerCase().includes('boutique');
        const isGrowth = currentPlan.name.toLowerCase().includes('growth');
        if (isBoutique) return p.name.toLowerCase().includes('boutique');
        if (isGrowth) return p.name.toLowerCase().includes('growth');
        return p.name.toLowerCase().includes('corporate');
      }
      return false;
    });

    setBillingPeriod(nextPeriod);
    if (matchingPlan) {
      setSelectedPlanId(matchingPlan.id);
    } else {
      const fallback = plans.find((p) => p.billing_period === nextPeriod);
      if (fallback) setSelectedPlanId(fallback.id);
    }
  };

  // Stripe Checkout Action
  const handleStripeCheckout = async () => {
    if (!selectedPlanId || isProcessingPayment) return;
    if (!createdOfferId) {
      showToastMessage('No se pudo vincular el pago con tu collab. Inténtalo de nuevo.', 'error');
      return;
    }
    const selectedPlanForCheckout = plans.find((plan) => plan.id === selectedPlanId);
    const requestedCreatorsForCheckout = Number(formData.quantity || 0);
    if (
      selectedPlanForCheckout
      && Number(selectedPlanForCheckout.max_creators_per_collab || 0) > 0
      && requestedCreatorsForCheckout > Number(selectedPlanForCheckout.max_creators_per_collab)
    ) {
      showToastMessage(
        `Este plan permite hasta ${selectedPlanForCheckout.max_creators_per_collab} creators por collab. Selecciona un plan compatible o reduce la cantidad.`,
        'error',
      );
      return;
    }

    setIsProcessingPayment(true);
    try {
      if (createdOfferId) {
        try {
          if (typeof api.patch === 'function') {
            await api.patch(`/collabs/collab/${createdOfferId}/`, { quantity: requestedCreatorsForCheckout });
          }
        } catch (err) {
          console.warn('Could not sync final quantity before checkout:', err);
        }
      }

      const response = await createStripeCheckoutSession({
        planId: selectedPlanId,
        offerId: createdOfferId,
      });

      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }
      showToastMessage('No se pudo generar la sesión de pago de Stripe.', 'error');
    } catch (err) {
      console.error('Checkout error:', err);
      showToastMessage(err.response?.data?.error || 'Error al conectar con Stripe.', 'error');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Plan filtering & calculations
  const filteredPlans = useMemo(() => {
    return plans.filter((p) => p.billing_period === billingPeriod);
  }, [plans, billingPeriod]);

  const selectedPlan = useMemo(() => {
    return plans.find((p) => p.id === selectedPlanId) || filteredPlans[0] || null;
  }, [plans, selectedPlanId, filteredPlans]);

  const requestedCreators = Number(formData.quantity || 0);
  const isPlanCompatible = useCallback((plan) => (
    Number(plan?.max_creators_per_collab || 0) === 0
    || requestedCreators <= Number(plan.max_creators_per_collab)
  ), [requestedCreators]);

  const hasDisabledPlans = useMemo(() => {
    return filteredPlans.some((p) => !isPlanCompatible(p));
  }, [filteredPlans, isPlanCompatible]);

  const handleModalQuantityChange = useCallback(async (newQty) => {
    const parsed = parseInt(newQty, 10);
    const validQty = Math.max(1, isNaN(parsed) ? 1 : parsed);
    setFormData((prev) => ({ ...prev, quantity: String(validQty) }));
    if (createdOfferId) {
      try {
        if (typeof api.patch === 'function') {
          await api.patch(`/collabs/collab/${createdOfferId}/`, { quantity: validQty });
        }
      } catch (err) {
        console.warn('Error syncing quantity to draft collab:', err);
      }
    }
  }, [createdOfferId]);

  const handleSelectPlanCard = (plan) => {
    if (isProcessingPayment) return;
    if (!isPlanCompatible(plan)) {
      if (Number(plan.max_creators_per_collab || 0) > 0) {
        handleModalQuantityChange(plan.max_creators_per_collab);
        setSelectedPlanId(plan.id);
        const cleanName = plan.name.replace(/ (6 Meses|Anual)$/i, '');
        showToastMessage(`Cupo ajustado a ${plan.max_creators_per_collab} creators para activar el plan ${cleanName}.`, 'info');
      }
      return;
    }
    setSelectedPlanId(plan.id);
  };

  useEffect(() => {
    if (!selectedPlanId || !plans.length || !selectedPlan) return;
    if (isPlanCompatible(selectedPlan)) return;
    const compatiblePlan = filteredPlans.find(isPlanCompatible) || plans.find(isPlanCompatible);
    setSelectedPlanId(compatiblePlan?.id || null);
  }, [filteredPlans, plans, selectedPlan, selectedPlanId, isPlanCompatible]);

  const formatPrice = (val) => `$${Number(val || 0).toFixed(2)}`;

  const getPlanPriceDisplay = (plan, period) => {
    const priceNum = Number(plan?.price || 0);
    if (period === 'semiannual') {
      const monthlyEquiv = priceNum / 6;
      return {
        monthlyPrice: formatPrice(monthlyEquiv),
        periodBilled: `${formatPrice(priceNum)} facturado cada 6 meses`,
        savingsBadge: 'Ahorras 5%',
      };
    }
    if (period === 'annual') {
      const monthlyEquiv = priceNum / 12;
      return {
        monthlyPrice: formatPrice(monthlyEquiv),
        periodBilled: `${formatPrice(priceNum)} facturado al año`,
        savingsBadge: 'Ahorras 10%',
      };
    }
    return {
      monthlyPrice: formatPrice(priceNum),
      periodBilled: 'Facturación mensual',
      savingsBadge: null,
    };
  };

  // Summary calculation
  const summaryValues = useMemo(() => {
    const isPaid = collabType === 'paid';
    const amount = isPaid ? Number(formData.sponsor_amount || 0) : Number(formData.value || 0);
    const qty = Math.max(1, Number(formData.quantity || 1));
    const total = amount * qty;

    const countryObj = countries.find(([id]) => String(id) === String(formData.country));
    const countryName = countryObj ? countryObj[1] : 'Por definir';

    const typeObj = TYPES.find(([id]) => String(id) === String(formData.type));
    const typeName = typeObj ? typeObj[1] : 'Por definir';

    const followerObj = FOLLOWER_REQUIREMENTS.find(([id]) => String(id) === String(formData.followers_requirements));
    const followerName = followerObj ? followerObj[1] : 'Sin requisitos';

    let deliverablesText = 'Por definir';
    if (formData.deliverables_mode === 'open') {
      deliverablesText = 'Abiertos (creator decide)';
    } else if (formData.exchange_options.length) {
      deliverablesText = formData.exchange_options.map((optId) => {
        const item = exchangeOptions.find((o) => String(o.id) === String(optId) || o.code === String(optId));
        const count = formData.deliverables_counts[optId] ?? 1;
        return `${item ? item.name : 'Entregable'} × ${count}`;
      }).join(', ');
    }

    const datesText = (formData.from_date && formData.to_date)
      ? `${formData.from_date} → ${formData.to_date}`
      : 'Por definir';

    const modeBadgeText = isPaid
      ? 'PAID COLLAB'
      : formData.sponsor_this_offer
      ? 'HYBRID COLLAB'
      : 'GIFTING COLLAB';

    return {
      total,
      amount,
      qty,
      countryName,
      typeName,
      followerName,
      deliverablesText,
      datesText,
      modeBadgeText,
    };
  }, [collabType, formData, countries, exchangeOptions]);

  // Creator access guard - evaluated after all hooks have run
  if (currentUser?.user_type === 'creator') {
    return <Navigate to="/creator/feed" replace />;
  }

  const isChoosingCollab = scene !== 'form';
  const isEmbedded = location.pathname !== '/business/first-collab';

  return (
    <div
      className={`first-collab-journey ${isChoosingCollab ? 'is-choosing-collab' : 'is-form'} scene-${scene} ${isEmbedded ? 'is-embedded' : 'is-standalone'}`}
      data-step={scene === 'form' ? String(currentStep) : '1'}
    >
      <div className="shell">
        {/* Sticky Topbar */}
        <header className="topbar">
          {!isEmbedded ? (
            <div className="brand" onClick={handleBackToOverview} role="button" tabIndex={0}>
              FOTH
            </div>
          ) : <div className="topbar-spacer" />}
          <div className="topbar-center">
            {scene === 'form' && (
              <div className="progressTop" aria-label="Progreso del primer collab">
                <span>Primer collab</span>
                <div className="progressTrack">
                  <div
                    className="progressFill"
                    style={{ width: `${(currentStep / 3) * 100}%` }}
                  />
                </div>
                <span>{currentStep} / 3</span>
              </div>
            )}
          </div>
          <button className="exit" type="button" onClick={handleExit}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
            Salir
          </button>
        </header>

        <div className={`workspace ${isChoosingCollab ? 'no-rail' : 'has-rail'}`}>
          {/* Rail Stepper (Visible in Form Scene) */}
          {scene === 'form' && (
            <aside className="rail" aria-label="Pasos de creación">
              <div className="railEyebrow">Tu primer collab</div>
              <ol>
                <li>
                  <button
                    type="button"
                    className={`${currentStep === 1 ? 'current' : ''} ${currentStep > 1 ? 'done' : ''}`}
                    onClick={() => setCurrentStep(1)}
                  >
                    <span className="dot">1</span>
                    <span>
                      <span className="labelMain">Detalles</span>
                      <span className="labelSub">Oferta y activación</span>
                    </span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className={`${currentStep === 2 ? 'current' : ''} ${currentStep > 2 ? 'done' : ''}`}
                    onClick={() => {
                      if (validateCurrentStepOnly(1)) setCurrentStep(2);
                    }}
                  >
                    <span className="dot">2</span>
                    <span>
                      <span className="labelMain">Requisitos</span>
                      <span className="labelSub">Audiencia y entregables</span>
                    </span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className={`${currentStep === 3 ? 'current' : ''}`}
                    onClick={() => {
                      if (validateCurrentStepOnly(1) && validateCurrentStepOnly(2)) setCurrentStep(3);
                    }}
                  >
                    <span className="dot">3</span>
                    <span>
                      <span className="labelMain">Assets y reglas</span>
                      <span className="labelSub">Imágenes y condiciones</span>
                    </span>
                  </button>
                </li>
              </ol>
              <div className="railFoot">
                Tu borrador se guarda automáticamente.<br />
                La publicación requiere suscripción.
              </div>
            </aside>
          )}

          <main className="stage">
            {/* SCENE 0: OVERVIEW */}
            {scene === 'overview' && (
              <section className="first-collab-overview-scene scene">
                <article className="activation-card">
                  <div className="activation-copy">
                    <span className="kicker">Business workspace · Tu primer collab</span>
                    <h2>Crea tu primer collab.</h2>
                    <p>
                      Diseña una propuesta atractiva en minutos con la asistencia inteligente de FOTH. Elige si colaborarás mediante producto, experiencia o pago en efectivo, y conecta con creadores verificados listos para impulsar tu marca.
                    </p>
                    <div className="activation-actions">
                      <button className="btn primary" type="button" onClick={handleStart}>
                        Crear mi primer collab
                        <ArrowRight size={16} />
                      </button>
                      <button className="btn secondary" type="button" onClick={() => handleExample('gifting')}>
                        Ver un ejemplo
                        <Eye size={15} />
                      </button>
                    </div>
                    <div className="activation-footnote">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="m5 12 4 4L19 6" />
                      </svg>
                      Crear el borrador es gratis · Publicas solo cuando estés listo
                    </div>
                  </div>
                  <div className="activation-visual" aria-label="Vista previa de selección de collab">
                    <div className="visual-card">
                      <div className="visual-card-head">
                        <span className="visual-spark">✦</span>
                        <span>Tu primer collab</span>
                        <span style={{ marginLeft: 'auto', color: 'rgba(255,255,255,.65)' }}>2 opciones</span>
                      </div>
                      <div className="visual-question">¿Qué recibirá el creator?</div>
                      <div className="visual-chip-row">
                        <span className="visual-chip">Producto o experiencia</span>
                        <span className="visual-chip">Pago en efectivo</span>
                      </div>
                      <div className="visual-progress">
                        <i />
                      </div>
                      <div className="trust-row">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M12 3 5 6v5c0 4.6 3 8.6 7 10 4-1.4 7-5.4 7-10V6l-7-3Z" />
                          <path d="m9 12 2 2 4-4" />
                        </svg>
                        Tú decides cuándo publicar
                      </div>
                    </div>
                  </div>
                </article>

                <div className="proof-row">
                  <div className="proof">
                    <div className="proof-icon" aria-hidden="true">
                      <CircleDollarSign size={18} />
                    </div>
                    <div className="proof-text">
                      <strong>Costes claros</strong>
                      <p>Verás suscripción y fees antes de pagar.</p>
                    </div>
                  </div>
                  <div className="proof">
                    <div className="proof-icon" aria-hidden="true">
                      <Sparkles size={18} />
                    </div>
                    <div className="proof-text">
                      <strong>Brief más rápido</strong>
                      <p>La IA ordena tus ideas en minutos.</p>
                    </div>
                  </div>
                  <div className="proof">
                    <div className="proof-icon" aria-hidden="true">
                      <ShieldCheck size={18} />
                    </div>
                    <div className="proof-text">
                      <strong>Control editorial</strong>
                      <p>Edita el preview antes de publicar.</p>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* SCENE 1: TYPE SELECTION */}
            {scene === 'type_selection' && (
              <section className="collab-type-selection scene" aria-labelledby="type-selection-title">
                <header className="type-selection-head">
                  <button className="back-link" type="button" onClick={handleBackToOverview}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M19 12H5M11 18l-6-6 6-6" />
                    </svg>
                    Volver al overview
                  </button>
                  <span className="eyebrow">Primer paso · Elige tu modelo</span>
                  <h1 id="type-selection-title" tabIndex={-1}>¿Qué recibirá el creator?</h1>
                  <p>Elige según la compensación principal de tu oferta. Así FOTH abrirá el formulario correcto y te pedirá solo los datos que aplican.</p>
                </header>

                <div className="type-choice-grid" role="group" aria-label="Tipos de colaboración">
                  {/* Gifting Card */}
                  <article className="type-choice-card">
                    <span className="type-card-top">
                      <span className="type-card-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <path d="M20 12v9H4v-9M2 7h20v5H2zM12 7v14M12 7H7.5a2.5 2.5 0 1 1 2.3-3.5L12 7Zm0 0h4.5a2.5 2.5 0 1 0-2.3-3.5L12 7Z" />
                        </svg>
                      </span>
                      <span className="type-card-badge">Producto, servicio o experiencia</span>
                    </span>
                    <h2>Gifting Collab</h2>
                    <p className="type-card-intro">Ofreces algo de tu marca como compensación principal, sin un pago en efectivo obligatorio por creator.</p>
                    <ul className="type-card-details">
                      <li>
                        <strong>Elige Gifting si…</strong>
                        Quieres invitar a probar un producto, una experiencia, una visita o un PR kit.
                      </li>
                      <li>
                        <strong>El creator recibe</strong>
                        El beneficio que ofreces. En el formulario indicarás su valor estimado y cuántos creators pueden participar.
                      </li>
                      <li>
                        <strong>Presupuesto</strong>
                        El valor del beneficio ayuda a describir la oferta; no es un pago en efectivo al creator.
                      </li>
                    </ul>
                    <button className="type-card-cta" type="button" onClick={() => handleSelectCollabType('gifting')}>
                      Continuar con Gifting Collab
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M5 12h14M13 6l6 6-6 6" />
                      </svg>
                    </button>
                  </article>

                  {/* Paid Card */}
                  <article className="type-choice-card">
                    <span className="type-card-top">
                      <span className="type-card-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                        </svg>
                      </span>
                      <span className="type-card-badge">Pago en efectivo por creator</span>
                    </span>
                    <h2>Paid Collab</h2>
                    <p className="type-card-intro">Defines un pago en efectivo por cada creator. El pago por creator es obligatorio para este tipo.</p>
                    <ul className="type-card-details">
                      <li>
                        <strong>Elige Paid si…</strong>
                        Ya tienes un fee o presupuesto en efectivo para remunerar el contenido del creator.
                      </li>
                      <li>
                        <strong>El creator recibe</strong>
                        El pago acordado por creator. También puedes describir un beneficio de producto o experiencia adicional.
                      </li>
                      <li>
                        <strong>Presupuesto</strong>
                        Verás la inversión de creators (cantidad × pago por creator). Los fees aplicables se mostrarán en checkout.
                      </li>
                    </ul>
                    <button className="type-card-cta" type="button" onClick={() => handleSelectCollabType('paid')}>
                      Continuar con Paid Collab
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M5 12h14M13 6l6 6-6 6" />
                      </svg>
                    </button>
                  </article>
                </div>

                <aside className="type-selection-tip">
                  <span className="tip-label">Regla rápida</span>
                  <strong>Si el creator recibirá dinero garantizado, elige Paid.</strong> Si recibirá principalmente un producto o experiencia, elige Gifting. Si ofreces ambos, empieza por Gifting y podrás añadir patrocinio en el formulario.
                </aside>
              </section>
            )}

            {/* SCENE 2: FORM VIEW */}
            {scene === 'form' && (
              <div className="collab-form-view">
                <div className="flow-header">
                  <div>
                    <button className="back-link" type="button" onClick={handleBackToOverview}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M19 12H5M11 18l-6-6 6-6" />
                      </svg>
                      Volver al overview
                    </button>
                    <h1>Crea tu primer collab</h1>
                    <p>Completa el formulario real de tu collab. FOTH AI puede ayudarte a rellenarlo y todo queda editable.</p>
                  </div>
                  <div className="flow-status">
                    <span className="save-dot" />
                    Borrador guardado automáticamente
                  </div>
                </div>

                <div className="form-layout">
                  {/* Left Form Step Container */}
                  <section className="panel real-form-panel">
                    <div className="real-form-head">
                      <div>
                        <span className="form-mode-badge">{summaryValues.modeBadgeText}</span>
                        <h2>{FORM_STEPS[currentStep - 1].title}</h2>
                        <p>{FORM_STEPS[currentStep - 1].subtitle}</p>
                      </div>
                      <button
                        ref={aiButtonRef}
                        id="open-ai-assistant"
                        className={`btn small ${!aiGuideDismissed ? 'ai-guide-target' : ''}`}
                        type="button"
                        onClick={handleOpenAI}
                        aria-haspopup="dialog"
                        aria-controls="assistant-modal"
                        aria-describedby={!aiGuideDismissed ? 'ai-guide-description' : undefined}
                      >
                        ✦ Autocompletar con FOTH AI
                      </button>
                    </div>

                    {/* Non-blocking AI Guide Coachmark */}
                    {!aiGuideDismissed && (
                      <aside className="ai-guide" id="ai-first-session-guide" role="group" aria-labelledby="ai-guide-title">
                        <span className="ai-guide-icon" aria-hidden="true">✦</span>
                        <div className="ai-guide-copy">
                          <strong id="ai-guide-title">¿Quieres ayuda para completar el formulario?</strong>
                          <p id="ai-guide-description">
                            FOTH AI puede convertir tus ideas en un primer brief y ayudarte con los campos. Después podrás revisar y editar cada respuesta.
                          </p>
                        </div>
                        <div className="ai-guide-actions">
                          <button className="btn primary" type="button" onClick={handleOpenAI}>
                            Usar asistente
                          </button>
                          <button className="btn ai-guide-dismiss" type="button" onClick={handleDismissAiGuide}>
                            Ahora no
                          </button>
                        </div>
                      </aside>
                    )}

                    <div className="form-step-content">
                      {/* STEP 1: DETALLES */}
                      {currentStep === 1 && (
                        <div className="form-step">
                          {/* Title */}
                          <div className="field span-2">
                            <label className="field-label" htmlFor="first-collab-title">
                              Título de la colaboración <span className="required-note">*</span>
                            </label>
                            <input
                              id="first-collab-title"
                              name="title"
                              className={`input ${errors.title ? 'error' : ''}`}
                              maxLength={100}
                              value={formData.title}
                              onChange={(e) => handleFieldChange('title', e.target.value)}
                              placeholder="Ej. Lanzamiento de colección cápsula"
                              aria-invalid={Boolean(errors.title)}
                              aria-describedby={errors.title ? 'first-collab-title-error' : undefined}
                              required
                            />
                            {errors.title && (
                              <span className="field-error" id="first-collab-title-error" role="alert">
                                {errors.title}
                              </span>
                            )}
                          </div>

                          {/* Amounts */}
                          {collabType === 'paid' ? (
                            <>
                              <div className="field">
                                <label className="field-label" htmlFor="first-collab-sponsor-amount">
                                  Pago en efectivo por creador (USD) <span className="required-note">*</span>
                                </label>
                                <div className="money-input">
                                  <span className="money-code">USD</span>
                                  <input
                                    id="first-collab-sponsor-amount"
                                    name="sponsor_amount"
                                    className={`input ${errors.sponsor_amount ? 'error' : ''}`}
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={formData.sponsor_amount}
                                    onChange={(e) => handleFieldChange('sponsor_amount', e.target.value)}
                                    placeholder="Ej. 150.00"
                                    aria-invalid={Boolean(errors.sponsor_amount)}
                                    aria-describedby={errors.sponsor_amount ? 'first-collab-sponsor-error' : 'first-collab-sponsor-help'}
                                    required
                                  />
                                </div>
                                <span className="field-help" id="first-collab-sponsor-help">
                                  Este es el pago que recibirá cada creator. Los fees de Paid Collab se calculan en checkout.
                                </span>
                                {errors.sponsor_amount && (
                                  <span className="field-error" id="first-collab-sponsor-error" role="alert">
                                    {errors.sponsor_amount}
                                  </span>
                                )}
                              </div>
                              <div className="field">
                                <label className="field-label" htmlFor="first-collab-value">
                                  Valor estimado del beneficio adicional <small>(opcional)</small>
                                </label>
                                <div className="money-input">
                                  <span className="money-code">USD</span>
                                  <input
                                    id="first-collab-value"
                                    name="value"
                                    className="input"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={formData.value}
                                    onChange={(e) => handleFieldChange('value', e.target.value)}
                                    placeholder="Ej. 100.00"
                                  />
                                </div>
                                <span className="field-help">No se suma al pago efectivo ni al checkout.</span>
                              </div>
                            </>
                          ) : (
                            <div className="field">
                              <label className="field-label" htmlFor="first-collab-value">
                                Valor estimado del beneficio (USD) <span className="required-note">*</span>
                              </label>
                              <div className="money-input">
                                <span className="money-code">USD</span>
                                <input
                                  id="first-collab-value"
                                  name="value"
                                  className={`input ${errors.value ? 'error' : ''}`}
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={formData.value}
                                  onChange={(e) => handleFieldChange('value', e.target.value)}
                                  placeholder="Ej. 120.00"
                                  aria-invalid={Boolean(errors.value)}
                                  aria-describedby={errors.value ? 'first-collab-value-error' : 'first-collab-value-help'}
                                  required
                                />
                              </div>
                              <span className="field-help" id="first-collab-value-help">
                                Valor aproximado del producto, consumo o experiencia que recibe el creator.
                              </span>
                              {errors.value && (
                                <span className="field-error" id="first-collab-value-error" role="alert">
                                  {errors.value}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Quantity */}
                          <div className="field">
                            <label className="field-label" htmlFor="first-collab-quantity">
                              Cantidad de creators <span className="required-note">*</span>
                            </label>
                            <input
                              id="first-collab-quantity"
                              name="quantity"
                              className={`input ${errors.quantity ? 'error' : ''}`}
                              type="number"
                              min="1"
                              value={formData.quantity}
                              onChange={(e) => handleFieldChange('quantity', e.target.value)}
                              aria-invalid={Boolean(errors.quantity)}
                              aria-describedby={errors.quantity ? 'first-collab-quantity-error' : 'first-collab-quantity-help'}
                              required
                            />
                            <span className="field-help" id="first-collab-quantity-help">
                              Número de creators que quieres activar en esta collab.
                            </span>
                            {errors.quantity && (
                              <span className="field-error" id="first-collab-quantity-error" role="alert">
                                {errors.quantity}
                              </span>
                            )}
                          </div>

                          {/* Dates */}
                          <div className="field span-2">
                            <span className="field-label" id="first-collab-dates-label">
                              Fechas de la colaboración <span className="required-note">*</span> <small>Máximo 30 días</small>
                            </span>
                            <div className="date-grid">
                              <input
                                id="first-collab-start-date"
                                className={`input ${errors.dates ? 'error' : ''}`}
                                type="date"
                                value={formatDateInputValue(formData.from_date)}
                                onChange={(e) => handleFieldChange('from_date', e.target.value)}
                                aria-label="Fecha de inicio"
                                aria-invalid={Boolean(errors.dates)}
                                required
                              />
                              <input
                                id="first-collab-end-date"
                                className={`input ${errors.dates ? 'error' : ''}`}
                                type="date"
                                value={formatDateInputValue(formData.to_date)}
                                onChange={(e) => handleFieldChange('to_date', e.target.value)}
                                aria-label="Fecha de fin"
                                aria-invalid={Boolean(errors.dates)}
                                required
                              />
                            </div>
                            {errors.dates && (
                              <span className="field-error" role="alert">
                                {errors.dates}
                              </span>
                            )}
                          </div>

                          {/* Country */}
                          <div className="field">
                            <label className="field-label" htmlFor="first-collab-country">
                              País <span className="required-note">*</span>
                            </label>
                            <select
                              id="first-collab-country"
                              name="country"
                              className={`select ${errors.country ? 'error' : ''}`}
                              value={formData.country}
                              onChange={(e) => handleFieldChange('country', e.target.value)}
                              aria-invalid={Boolean(errors.country)}
                              aria-describedby={errors.country ? 'first-collab-country-error' : undefined}
                              required
                            >
                              <option value="">Selecciona un país</option>
                              {countries.map(([id, countryName]) => (
                                <option key={id} value={id}>
                                  {countryName}
                                </option>
                              ))}
                            </select>
                            {errors.country && (
                              <span className="field-error" id="first-collab-country-error" role="alert">
                                {errors.country}
                              </span>
                            )}
                          </div>

                          {/* Description */}
                          <div className="field">
                            <label className="field-label" htmlFor="first-collab-description">
                              Descripción <span className="required-note">*</span>
                            </label>
                            <textarea
                              id="first-collab-description"
                              name="description"
                              className={`textarea ${errors.description ? 'error' : ''}`}
                              value={formData.description}
                              onChange={(e) => handleFieldChange('description', e.target.value)}
                              placeholder="Describe los detalles de tu colaboración..."
                              aria-invalid={Boolean(errors.description)}
                              aria-describedby={errors.description ? 'first-collab-description-error' : undefined}
                              required
                            />
                            {errors.description && (
                              <span className="field-error" id="first-collab-description-error" role="alert">
                                {errors.description}
                              </span>
                            )}
                          </div>

                          {/* Time range toggle */}
                          <div className="field span-2">
                            <div className="toggle-row">
                              <div className="toggle-copy">
                                <strong>Incluye rango horario</strong>
                                <span>Muestra horas específicas para esta colaboración.</span>
                              </div>
                              <label className="toggle">
                                <input
                                  type="checkbox"
                                  checked={Boolean(formData.add_time_range)}
                                  onChange={(e) => handleToggleChange('add_time_range', e.target.checked)}
                                />
                                <span className="toggle-track" aria-hidden="true" />
                              </label>
                            </div>
                          </div>

                          {/* Collab Category / Type Radio Grid */}
                          <div className="field span-2">
                            <span className="field-label" id="first-collab-type-label">
                              Tipo de colaboración <span className="required-note">*</span>
                            </span>
                            <div className="radio-grid" role="radiogroup" aria-labelledby="first-collab-type-label">
                              {TYPES.map(([id, label]) => (
                                <div key={id} className="radio-card">
                                  <input
                                    id={`first-collab-type-${id}`}
                                    type="radio"
                                    name="collab-type"
                                    value={id}
                                    checked={String(formData.type) === String(id)}
                                    onChange={(e) => handleFieldChange('type', e.target.value)}
                                  />
                                  <label htmlFor={`first-collab-type-${id}`}>{label}</label>
                                </div>
                              ))}
                            </div>
                            {errors.type && (
                              <span className="field-error" role="alert">
                                {errors.type}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* STEP 2: REQUISITOS */}
                      {currentStep === 2 && (
                        <div className="form-step">
                          {/* Followers Requirements */}
                          <div className="field span-2">
                            <span className="field-label" id="first-collab-followers-label">
                              Requisitos de seguidores
                            </span>
                            <div className="radio-grid" role="radiogroup" aria-labelledby="first-collab-followers-label">
                              {FOLLOWER_REQUIREMENTS.map(([id, label]) => (
                                <div key={id} className="radio-card">
                                  <input
                                    id={`first-collab-followers-${id}`}
                                    type="radio"
                                    name="collab-followers"
                                    value={id}
                                    checked={String(formData.followers_requirements) === String(id)}
                                    onChange={(e) => handleFieldChange('followers_requirements', e.target.value)}
                                  />
                                  <label htmlFor={`first-collab-followers-${id}`}>{label}</label>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Deliverables Mode */}
                          <div className="field span-2">
                            <span className="field-label">Requisitos de entregables</span>
                            <div className="choice-group">
                              <button
                                type="button"
                                className={`choice ${formData.deliverables_mode === 'open' ? 'selected' : ''}`}
                                onClick={() => handleDeliverablesModeChange('open')}
                              >
                                Open Deliverables
                              </button>
                              <button
                                type="button"
                                className={`choice ${formData.deliverables_mode === 'defined' ? 'selected' : ''}`}
                                onClick={() => handleDeliverablesModeChange('defined')}
                              >
                                Defined Requirements
                              </button>
                            </div>
                            <span className="field-help">
                              {formData.deliverables_mode === 'open'
                                ? 'Los creators pueden publicar el contenido que mejor represente la experiencia.'
                                : 'Define cantidades exactas para Posts, Stories y Reels.'}
                            </span>
                          </div>

                          {/* Deliverables Format List */}
                          <div className="field span-2">
                            <span className="field-label">
                              {formData.deliverables_mode === 'open'
                                ? 'Formatos de contenido preferidos'
                                : 'Opciones de intercambio (entregables)'}
                            </span>
                            <div className="deliverable-list">
                              {exchangeOptions.map((opt) => {
                                const idStr = String(opt.id);
                                const isSelected = formData.exchange_options.includes(idStr);
                                return (
                                  <div
                                    key={opt.id}
                                    className={`deliverable-row ${isSelected ? 'selected' : ''}`}
                                  >
                                    <button
                                      type="button"
                                      className="deliverable-main"
                                      onClick={() => handleDeliverableToggle(idStr)}
                                      aria-pressed={isSelected}
                                    >
                                      <span className="deliverable-check">✓</span>
                                      {opt.name}
                                    </button>
                                    {isSelected && formData.deliverables_mode === 'defined' && (
                                      <input
                                        className="count-input"
                                        type="number"
                                        min="0"
                                        value={formData.deliverables_counts[idStr] ?? 1}
                                        onChange={(e) => handleDeliverableCountChange(idStr, e.target.value)}
                                        aria-label={`Cantidad de ${opt.name}`}
                                      />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <span className="field-help">
                              {formData.deliverables_mode === 'open'
                                ? 'Selecciona formatos solo si quieres señalar una preferencia.'
                                : 'Selecciona al menos un formato e indica la cantidad.'}
                            </span>
                            {(errors.exchange_options || errors.deliverables_counts) && (
                              <span className="field-error" role="alert">
                                {errors.exchange_options || errors.deliverables_counts}
                              </span>
                            )}
                          </div>

                          {/* Target Audience */}
                          <div className="field span-2">
                            <span className="field-label">Público objetivo</span>
                            <div className="choice-group">
                            {targetOptions.map((target) => {
                                const idStr = String(target.id);
                                const isSelected = formData.target_options.includes(idStr);
                                return (
                                  <button
                                    key={target.id}
                                    type="button"
                                    className={`choice ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleTargetToggle(idStr)}
                                    aria-pressed={isSelected}
                                  >
                                    {target.name}
                                  </button>
                                );
                              })}
                            </div>
                            <span className="field-help">Puedes seleccionar varios perfiles de audiencia.</span>
                            {errors.target_options && (
                              <span className="field-error" role="alert">
                                {errors.target_options}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* STEP 3: ASSETS Y CONDICIONES (High Contrast Dark Luxury) */}
                      {currentStep === 3 && (
                        <div className="form-step">
                          {/* Featured Image */}
                          <div className="field span-2">
                            <label className="field-label" htmlFor="first-collab-featuredImageInput">
                              Imagen destacada <span className="required-note">*</span>
                            </label>
                            <div className="featured-drop">
                              {featuredPreviewUrl ? (
                                <div className="image-preview-card">
                                  <div className="image-preview-media">
                                    <img
                                      src={featuredPreviewUrl}
                                      alt={selectedFeaturedFile ? selectedFeaturedFile.name : 'Vista previa de la imagen destacada'}
                                    />
                                  </div>
                                  <div className="image-preview-copy">
                                    <span className="upload-status">Imagen adjunta</span>
                                    <strong>{selectedFeaturedFile ? selectedFeaturedFile.name : 'Imagen lista'}</strong>
                                    <span>Esta imagen se usará como portada de tu collab.</span>
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                                      <label className="file-picker-button" htmlFor="first-collab-featuredImageInput">
                                        Cambiar imagen
                                      </label>
                                      <button
                                        type="button"
                                        className="btn ghost small"
                                        onClick={handleRemoveFeaturedImage}
                                        style={{ color: '#ffb4aa' }}
                                      >
                                        Eliminar
                                      </button>
                                    </div>
                                    <span className="file-status" role="status" aria-live="polite">
                                      Archivo seleccionado: {selectedFeaturedFile?.name || 'imagen.jpg'}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <div className="featured-empty-state">
                                  <span className="featured-empty-icon" aria-hidden="true">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                                      <rect x="3" y="4" width="18" height="16" rx="2" />
                                      <circle cx="8.5" cy="9" r="1.5" />
                                      <path d="m4 17 4.5-4 3.5 3 2.5-2 5.5 4" />
                                    </svg>
                                  </span>
                                  <strong>Selecciona la imagen principal</strong>
                                  <span>Una imagen clara ayuda a los creators a entender tu oferta. JPG o PNG · 1 imagen obligatoria.</span>
                                  <label className="file-picker-button" htmlFor="first-collab-featuredImageInput">
                                    Seleccionar imagen destacada
                                  </label>
                                  <span className="file-status" role="status" aria-live="polite">
                                    No has seleccionado un archivo.
                                  </span>
                                </div>
                              )}
                              <input
                                ref={fileInputRef}
                                id="first-collab-featuredImageInput"
                                className="file-input-hidden"
                                type="file"
                                accept="image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png"
                                onChange={handleFeaturedImageUpload}
                                aria-invalid={Boolean(errors.featured_image)}
                              />
                            </div>
                            {errors.featured_image && (
                              <span className="field-error" role="alert" style={{ color: '#ffb4aa' }}>
                                {errors.featured_image}
                              </span>
                            )}
                          </div>

                          {/* Additional Images */}
                          <div className="field span-2">
                            <label className="field-label" htmlFor="first-collab-additionalImagesInput">
                              Imágenes adicionales <small>(opcional, hasta 5 JPG/PNG)</small>
                            </label>
                            <div className="image-drop" style={{ background: '#17171b', border: '1px solid #4b4434', borderRadius: '14px', padding: '16px' }}>
                              <label className="file-picker-button" htmlFor="first-collab-additionalImagesInput">
                                Seleccionar imágenes
                              </label>
                              <span className="file-status" style={{ display: 'block', marginTop: '6px' }}>
                                {selectedAdditionalFiles.length ? `${selectedAdditionalFiles.length} de 5 imágenes seleccionadas.` : 'No has seleccionado imágenes adicionales.'}
                              </span>
                              <input
                                ref={additionalFileInputRef}
                                id="first-collab-additionalImagesInput"
                                className="file-input-hidden"
                                type="file"
                                accept="image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png"
                                multiple
                                onChange={handleAdditionalImagesUpload}
                              />
                              {selectedAdditionalFiles.length > 0 && (
                                <div className="file-list">
                                  {selectedAdditionalFiles.map((f, idx) => (
                                    <span key={idx} className="file-chip">
                                      {f.name}
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveAdditionalImage(idx)}
                                        style={{ marginLeft: '6px', background: 'transparent', border: 0, color: '#ffb4aa', cursor: 'pointer' }}
                                        aria-label={`Eliminar imagen ${f.name}`}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Terms & Conditions */}
                          <div className="field span-2">
                            <label className="field-label" htmlFor="first-collab-terms">
                              Términos y condiciones <span className="required-note">*</span>
                            </label>
                            <textarea
                              id="first-collab-terms"
                              className={`textarea ${errors.terms_and_conditions ? 'error' : ''}`}
                              value={formData.terms_and_conditions}
                              onChange={(e) => handleFieldChange('terms_and_conditions', e.target.value)}
                              placeholder="Especifica las reglas y condiciones que el creator debe cumplir..."
                              aria-invalid={Boolean(errors.terms_and_conditions)}
                              required
                            />
                            <span className="field-help">Describe las reglas, los requisitos de publicación y cualquier condición que el creator deba conocer.</span>
                            {errors.terms_and_conditions && (
                              <span className="field-error" role="alert" style={{ color: '#ffb4aa' }}>
                                {errors.terms_and_conditions}
                              </span>
                            )}
                          </div>

                          {/* Sponsorship Section */}
                          {collabType === 'paid' && (
                            <div className="field span-2">
                              <div className="field-note">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                                  <circle cx="12" cy="12" r="9" />
                                  <path d="M12 10v6M12 7h.01" />
                                </svg>
                                <span>
                                  <strong>Paid Collab:</strong> el pago por creator ya está definido en el paso 1. Los fees aplicables se mostrarán antes de confirmar el pago.
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Step Actions */}
                    <div className="form-actions">
                      <button
                        className="btn ghost"
                        type="button"
                        onClick={handlePrevStep}
                        disabled={currentStep === 1}
                      >
                        Atrás
                      </button>
                      <div className="form-actions-right">
                        <button
                          className="btn dark"
                          type="button"
                          onClick={handleSaveDraft}
                          disabled={isSavingDraft}
                        >
                          {isSavingDraft ? 'Guardando…' : 'Guardar borrador'}
                        </button>
                        <button
                          className="btn primary"
                          type="button"
                          onClick={handleNextStep}
                          disabled={isSavingDraft}
                        >
                          {currentStep === 3 ? 'Revisar y publicar' : 'Continuar'}
                        </button>
                      </div>
                    </div>
                  </section>

                  {/* Right Summary Sidebar */}
                  <aside className="panel summary-panel">
                    <div className="summary-head">
                      <strong>Resumen del collab</strong>
                      <span>Se actualiza con los datos del formulario.</span>
                    </div>
                    <div className="summary-body">
                      <span className="summary-mode">{summaryValues.modeBadgeText}</span>
                      <div className="summary-list">
                        <div className="summary-item">
                          <span>Título</span>
                          <strong>{formData.title || 'Por definir'}</strong>
                        </div>
                        <div className="summary-item">
                          <span>Tipo</span>
                          <strong>{summaryValues.typeName}</strong>
                        </div>
                        <div className="summary-item">
                          <span>País</span>
                          <strong>{summaryValues.countryName}</strong>
                        </div>
                        <div className="summary-item">
                          <span>Creators</span>
                          <strong>{formData.quantity || '1'}</strong>
                        </div>
                        <div className="summary-item">
                          <span>Audiencia</span>
                          <strong>{summaryValues.followerName}</strong>
                        </div>
                        <div className="summary-item">
                          <span>Entregables</span>
                          <strong>{summaryValues.deliverablesText}</strong>
                        </div>
                        <div className="summary-item">
                          <span>Fechas</span>
                          <strong>{summaryValues.datesText}</strong>
                        </div>
                      </div>

                      {summaryValues.total > 0 && (
                        <div className="investment-card">
                          <span>{collabType === 'paid' ? 'Pago estimado' : 'Valor estimado'}</span>
                          <strong>{formatPrice(summaryValues.total)}</strong>
                          <p>
                            {summaryValues.qty} creator{summaryValues.qty === 1 ? '' : 's'} × {formatPrice(summaryValues.amount)} por creator. Fees e impuestos se calcularán en el pago.
                          </p>
                        </div>
                      )}

                      <div className="field-note">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                          <circle cx="12" cy="12" r="9" />
                          <path d="M12 10v6M12 7h.01" />
                        </svg>
                        <span>
                          El borrador es editable. Publicar requiere una suscripción activa
                          {(collabType === 'paid' || formData.sponsor_this_offer) ? ' y los fees de Paid Collab se calcularán antes del pago' : ''}.
                        </span>
                      </div>
                    </div>
                  </aside>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* AICampaignAssistant Integration */}
      <AICampaignAssistant
        isOpen={isAIAssistantOpen}
        onClose={handleCloseAI}
        onApplyData={handleApplyAIData}
        currentFormData={formData}
        mode={collabType}
        compensationMode={collabType === 'paid' ? 'paid' : (formData.sponsor_this_offer ? 'hybrid' : 'exchange')}
        countries={countries}
      />

      {/* Pricing Modal */}
      {isPricingModalOpen && (
        <div
          className="modal-backdrop pricing-modal-backdrop is-open"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pricing-title"
          aria-describedby="pricing-subtitle"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isProcessingPayment) {
              handleClosePricingModal();
            }
          }}
        >
          <div className="pricing-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-scrollable-body" tabIndex={0} role="region" aria-label="Comparar planes y condiciones">
            <div className="modal-top">
              <div>
                <span className="eyebrow">Último paso antes de publicar</span>
                <h2 id="pricing-title">Elige el plan para activar tu collab.</h2>
                <p id="pricing-subtitle">
                  Tu brief ya está listo. Selecciona uno de los planes de FOTH para activar la publicación y conectar con creadores.
                </p>
              </div>
              <button
                className="close-button"
                type="button"
                onClick={handleClosePricingModal}
                aria-label="Cerrar modal de planes"
                disabled={isProcessingPayment}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M6 6l12 12M18 6 6 18" />
                </svg>
              </button>
            </div>

            {/* Segmented Pill Selector for Billing Period */}
            <div className="billing-period-tabs">
              <div className="billing-period-pill-group" role="tablist" aria-label="Períodos de facturación">
                <button
                  type="button"
                  className={`period-tab ${billingPeriod === 'monthly' ? 'active' : ''}`}
                  onClick={() => handleSelectBillingPeriod('monthly')}
                  role="tab"
                  aria-selected={billingPeriod === 'monthly'}
                  disabled={isProcessingPayment}
                >
                  Mensual
                </button>
                <button
                  type="button"
                  className={`period-tab ${billingPeriod === 'semiannual' ? 'active' : ''}`}
                  onClick={() => handleSelectBillingPeriod('semiannual')}
                  role="tab"
                  aria-selected={billingPeriod === 'semiannual'}
                  disabled={isProcessingPayment}
                >
                  6 Meses <span className="save-badge">-5%</span>
                </button>
                <button
                  type="button"
                  className={`period-tab ${billingPeriod === 'annual' ? 'active' : ''}`}
                  onClick={() => handleSelectBillingPeriod('annual')}
                  role="tab"
                  aria-selected={billingPeriod === 'annual'}
                  disabled={isProcessingPayment}
                >
                  Anual <span className="save-badge">-10%</span>
                </button>
              </div>
            </div>

            {/* Creators Context & Explanation Banner */}
            <div className={`pricing-creators-banner ${hasDisabledPlans ? 'has-restrictions' : ''}`}>
              <div className="banner-left">
                <div className="banner-headline">
                  {hasDisabledPlans ? <AlertCircle size={16} aria-hidden="true" /> : <Users size={16} aria-hidden="true" />}
                  <span>
                    {hasDisabledPlans
                      ? 'Plan seleccionado según la cantidad de creadores'
                      : `${requestedCreators} ${requestedCreators === 1 ? 'creador solicitado' : 'creadores solicitados'}`}
                  </span>
                </div>
                <p className="banner-text">
                  {hasDisabledPlans ? (
                    <>
                      Tu collab solicita <strong>{requestedCreators} creadores</strong>. Seleccionamos automáticamente el plan <strong>{selectedPlan?.name?.replace(/ (6 Meses|Anual)$/i, '') || 'Corporate'}</strong> porque es el que admite esta capacidad. Los planes con cupo menor están deshabilitados. Puedes rebajar la cantidad aquí o hacer clic en un plan para ajustarlo automáticamente.
                    </>
                  ) : (
                    <>
                      Tu collab solicita <strong>{requestedCreators} {requestedCreators === 1 ? 'creador' : 'creadores'}</strong>, compatible con todos los planes disponibles.
                    </>
                  )}
                </p>
              </div>

              <div className="banner-right">
                <label htmlFor="modal-creators-input" className="adjust-label">
                  Rebajar / ajustar creadores:
                </label>
                <div className="creators-stepper">
                  <button
                    type="button"
                    className="stepper-btn"
                    onClick={() => handleModalQuantityChange(Math.max(1, requestedCreators - 1))}
                    disabled={requestedCreators <= 1 || isProcessingPayment}
                    aria-label="Disminuir un creador"
                  >
                    <Minus size={13} />
                  </button>
                  <input
                    id="modal-creators-input"
                    className="stepper-input"
                    type="number"
                    min="1"
                    max="999"
                    value={formData.quantity}
                    onChange={(e) => handleModalQuantityChange(e.target.value)}
                    disabled={isProcessingPayment}
                    aria-label="Cantidad de creadores del collab"
                  />
                  <button
                    type="button"
                    className="stepper-btn"
                    onClick={() => handleModalQuantityChange(requestedCreators + 1)}
                    disabled={isProcessingPayment}
                    aria-label="Aumentar un creador"
                  >
                    <Plus size={13} />
                  </button>
                </div>
              </div>
            </div>

            {/* Plan comparison shares the scroll area with its context. */}
              <div className="plan-grid" role="radiogroup" aria-label="Planes de suscripción">
                {filteredPlans.map((plan) => {
                  const isSelected = selectedPlanId === plan.id;
                  const isCompatible = isPlanCompatible(plan);
                  const isGrowth = plan.name.toLowerCase().includes('growth');
                  const isBoutique = plan.name.toLowerCase().includes('boutique');
                  const isCorporate = plan.name.toLowerCase().includes('corporate');
                  const { monthlyPrice, periodBilled } = getPlanPriceDisplay(plan, billingPeriod);
                  const cleanName = plan.name.replace(/ (6 Meses|Anual)$/i, '');

                  return (
                    <article
                      key={plan.id}
                      className={`plan-card ${isGrowth ? 'recommended' : ''} ${isSelected ? 'selected' : ''} ${!isCompatible ? 'disabled' : ''}`}
                      onClick={() => handleSelectPlanCard(plan)}
                      tabIndex={0}
                      role="radio"
                      aria-checked={isSelected}
                      aria-disabled={!isCompatible}
                      aria-label={`${cleanName}, ${monthlyPrice} al mes, ${periodBilled}`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSelectPlanCard(plan);
                        }
                      }}
                    >
                      <div className="plan-card-head">
                        <span className={`plan-badge ${isGrowth ? 'recommended' : ''}`}>
                          {isGrowth
                            ? '✦ MÁS ELEGIDO · RECOMENDADO'
                            : isBoutique
                            ? 'PARA COMENZAR'
                            : 'PARA EQUIPOS'}
                        </span>
                        {!isCompatible && Number(plan.max_creators_per_collab || 0) > 0 && (
                          <span className="plan-incompatible-tag">
                            Máx. {plan.max_creators_per_collab} creators
                          </span>
                        )}
                      </div>

                      <h3>{cleanName}</h3>
                      <p className="plan-desc">{plan.description}</p>

                      <div className="plan-price-block">
                        <div className="main-rate">
                          <strong>{monthlyPrice}</strong>
                          <span>/ mes</span>
                        </div>
                        <div className="billed-note">{periodBilled}</div>
                      </div>

                      <ul className="plan-list">
                        <li>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                            <path d="m5 12 4 4L19 6" />
                          </svg>
                          <span>
                            {plan.max_collabs === 0 ? 'Collabs de canje ilimitados' : `${plan.max_collabs} collabs de canje activas`}
                          </span>
                        </li>
                        <li>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                            <path d="m5 12 4 4L19 6" />
                          </svg>
                          <span>{plan.max_collabs === 0 ? 'Collabs ilimitados' : `${plan.max_collabs} collabs activas`}</span>
                        </li>
                        <li>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                            <path d="m5 12 4 4L19 6" />
                          </svg>
                          <span>
                            {plan.max_creators_per_collab === 0 ? 'Creators ilimitados por collab' : `Hasta ${plan.max_creators_per_collab} creators por collab`}
                          </span>
                        </li>
                        <li>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                            <path d="m5 12 4 4L19 6" />
                          </svg>
                          <span>Dashboard de gestión y analíticas</span>
                        </li>
                        {isCorporate && (
                          <li>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                              <path d="m5 12 4 4L19 6" />
                            </svg>
                            <span>Soporte prioritario dedicado</span>
                          </li>
                        )}
                      </ul>

                      {!isCompatible && Number(plan.max_creators_per_collab || 0) > 0 ? (
                        <div
                          className="card-select-btn disabled-btn"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectPlanCard(plan);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleSelectPlanCard(plan);
                            }
                          }}
                        >
                          Ajustar a {plan.max_creators_per_collab} y elegir
                        </div>
                      ) : (
                        <div className={`card-select-btn ${isSelected ? 'selected' : ''}`} aria-hidden="true">
                          {isSelected ? (
                            <>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="m5 12 4 4L19 6" />
                              </svg>
                              Plan seleccionado
                            </>
                          ) : (
                            'Seleccionar este plan'
                          )}
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
              <p className="mobile-plan-context">
                Creadores: {requestedCreators} · {billingPeriod === 'monthly' ? 'Facturación mensual' : billingPeriod === 'semiannual' ? 'Facturación cada 6 meses (-5%)' : 'Facturación anual (-10%)'}
                {' · '}{collabType === 'paid' ? 'Fees del collab calculados en checkout' : 'Sin comisiones por creador (Gifting)'}
              </p>
              <details className="fee-note">
                <summary>Transparencia de costes FOTH</summary>
                <div>
                  <p>
                    La suscripción activa la plataforma y la publicación de tus ofertas.
                    {collabType === 'paid'
                      ? ' Para Paid Collab, el pago a creators se calcula y confirma de forma segura en Stripe.'
                      : ' En Gifting Collab no hay comisiones por creador.'}
                  </p>
                </div>
              </details>
            </div>

            {/* Compact persistent checkout summary. */}
            <div className="modal-bottom">
              <div className="order-summary">
                <div className="summary-row">
                  <span>Plan: {selectedPlan ? selectedPlan.name.replace(/ (6 Meses|Anual)$/i, '') : 'Seleccionado'}</span>
                  <strong>{formatPrice(selectedPlan?.price)}</strong>
                </div>
                <div className="summary-row">
                  <span>Creadores en collab</span>
                  <strong>{requestedCreators} {requestedCreators === 1 ? 'creator' : 'creators'}</strong>
                </div>
                <div className="summary-row">
                  <span>Período</span>
                  <span>{billingPeriod === 'monthly' ? 'Mensual' : billingPeriod === 'semiannual' ? '6 Meses (-5%)' : 'Anual (-10%)'}</span>
                </div>
                <div className="summary-row">
                  <span>Fees del collab</span>
                  <span>{collabType === 'paid' ? 'Calculado en checkout' : 'No aplica (Gifting)'}</span>
                </div>
                <div className="summary-row total">
                  <span>Total hoy</span>
                  <strong>
                    {collabType === 'paid'
                      ? `${formatPrice(selectedPlan?.price)} + fees`
                      : formatPrice(selectedPlan?.price)}
                  </strong>
                </div>

                <button
                  className="btn primary pay-button"
                  type="button"
                  onClick={handleStripeCheckout}
                  disabled={isProcessingPayment || !selectedPlanId || !selectedPlan || !isPlanCompatible(selectedPlan)}
                >
                  {isProcessingPayment ? (
                    <>
                      <span className="pay-button-spinner" aria-hidden="true" />
                      Conectando con Stripe…
                    </>
                  ) : (
                    <>
                      Continuar al pago con Stripe
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M5 12h14M13 6l6 6-6 6" />
                      </svg>
                    </>
                  )}
                </button>

                <div className="secure-note">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                    <rect x="5" y="10" width="14" height="11" rx="2" />
                    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
                  </svg>
                  Pago seguro SSL con Stripe · Cancela cuando quieras
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* In-page Toast Notification */}
      {toast.visible && (
        <div className={`toast ${toast.type === 'error' ? 'is-error' : ''}`} role="status">
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default FirstCollabActivation;
